import { z } from 'zod';
/**
 * App 3 contracts (spec §11), shared by the audit screens and the routes.
 *
 * App 3's shape differs from the other two in a way worth stating once here,
 * because it explains most of what follows: **an audit is a long-lived
 * engagement, not a document**. App 1 submits a monthly package and App 2
 * submits a form, but an audit is scheduled months ahead, planned, executed over
 * days, and then closed once every corrective action it produced has been
 * verified. Several of the fields below are therefore optional at creation and
 * required only at a particular gate — and each gate says which.
 */
/** The three lifecycle phases (spec §11). */
export const auditPhaseSchema = z.enum(['pre_audit', 'execution', 'post_audit']);
export const auditStatusSchema = z.enum([
    'scheduled',
    'pending_approval',
    'approved',
    'sent_back',
    'in_progress',
    'reporting',
    'closed',
]);
const title = z.string().trim().min(1, 'A title is required.').max(300);
const longText = z.string().trim().max(10000);
/**
 * A calendar year, bounded rather than open.
 *
 * Not because 1970 is impossible, but because a typo in an annual schedule year
 * is silent: the schedule saves, and nobody notices until the audits it holds
 * fail to appear on this year's dashboard. The window is generous and the
 * message says what it is for.
 */
const auditYear = z
    .number()
    .int()
    .min(2000, 'That year looks like a typo.')
    .max(2100, 'That year looks like a typo.');
// ---------------------------------------------------------------------------
// Pre-Audit: the annual schedule
// ---------------------------------------------------------------------------
export const auditScheduleCreateSchema = z.object({
    year: auditYear,
    title,
});
export const auditScheduleUpdateSchema = auditScheduleCreateSchema.partial();
/**
 * Send-back demands a reason, at the contract rather than in the form.
 *
 * Same rule as App 1 and App 2: an Audit Program Manager told only that their
 * annual schedule was rejected has been given a verdict, not a correction. The
 * form can enforce it too, but the form is not the only way in.
 */
export const auditSendBackSchema = z.object({
    remarks: z.string().trim().min(1, 'Say what needs to change before sending this back.').max(4000),
});
// ---------------------------------------------------------------------------
// Pre-Audit: the plan
// ---------------------------------------------------------------------------
export const auditPlanCreateSchema = z.object({
    scheduleId: z.string().uuid().nullable().optional(),
    title,
    scope: longText.optional(),
    criteria: longText.optional(),
    plannedStartDate: z.string().date().nullable().optional(),
    plannedEndDate: z.string().date().nullable().optional(),
    location: z.string().trim().max(300).nullable().optional(),
    feasibilityNotes: longText.optional(),
});
export const auditPlanUpdateSchema = auditPlanCreateSchema.partial();
// ---------------------------------------------------------------------------
// The engagement
// ---------------------------------------------------------------------------
export const auditCreateSchema = z.object({
    planId: z.string().uuid().nullable().optional(),
    projectId: z.string().uuid(),
    title,
    leadAuditorId: z.string().uuid().nullable().optional(),
    /**
     * The audit team. A plain list of user ids, deduplicated server-side.
     *
     * Spec §11 puts Team Assignment in Pre-Audit, so this is set before the audit
     * starts and can change until it does.
     */
    teamUserIds: z.array(z.string().uuid()).max(50).optional(),
    actualStartDate: z.string().date().nullable().optional(),
    actualEndDate: z.string().date().nullable().optional(),
});
export const auditUpdateSchema = auditCreateSchema.partial().omit({ projectId: true });
/**
 * Meeting minutes.
 *
 * Opening and closing meetings are separate acts with separate timestamps, so
 * this takes which one it is rather than inferring it from the audit's phase. An
 * audit that has to be reopened must not have its opening minutes overwritten
 * because the phase happened to read `execution` at the time.
 */
export const auditMinutesSchema = z.object({
    meeting: z.enum(['opening', 'closing']),
    heldAt: z.string().datetime({ offset: true }),
    minutes: longText,
});
// ---------------------------------------------------------------------------
// Audit Execution: checklists
// ---------------------------------------------------------------------------
export const checklistResponseValueSchema = z.enum([
    'conforms',
    'does_not_conform',
    'not_applicable',
    'unanswered',
]);
export const checklistTemplateCreateSchema = z.object({
    name: z.string().trim().min(1, 'Give the checklist a name.').max(200),
    description: longText.optional(),
    auditTypeKey: z.string().trim().max(100).nullable().optional(),
});
export const checklistItemCreateSchema = z.object({
    clause: z.string().trim().max(50).nullable().optional(),
    sequence: z.number().int().min(0).max(10000).optional(),
    question: z.string().trim().min(1, 'A checklist item needs a question.').max(2000),
    guidance: longText.optional(),
});
/**
 * Answering one checklist item.
 *
 * `notes` is required when the answer is `does_not_conform`, and that is
 * enforced here rather than only in the form. A bare "does not conform" with no
 * words is not evidence of anything — the auditor who wrote it will be asked, in
 * six months, what they actually saw.
 */
export const checklistAnswerSchema = z
    .object({
    templateItemId: z.string().uuid(),
    response: checklistResponseValueSchema,
    notes: longText.optional(),
})
    .refine((value) => value.response !== 'does_not_conform' || Boolean(value.notes?.trim()), { message: 'Say what you observed before marking an item non-conforming.', path: ['notes'] });
// ---------------------------------------------------------------------------
// Audit Execution: evidence
// ---------------------------------------------------------------------------
export const evidenceTypeSchema = z.enum(['document', 'photo', 'interview', 'observation']);
/** Same four outcomes as App 2 — a failed fix records WHY, and never blocks. */
export const auditGpsCaptureStatusSchema = z.enum([
    'captured',
    'denied',
    'unavailable',
    'timed_out',
]);
export const evidenceCreateSchema = z.object({
    /** Client-generated, so evidence captured offline keeps its identity through sync. */
    id: z.string().uuid().optional(),
    checklistResponseId: z.string().uuid().nullable().optional(),
    evidenceType: evidenceTypeSchema,
    description: longText.optional(),
    fileId: z.string().uuid().nullable().optional(),
    gpsLatitude: z.number().min(-90).max(90).nullable().optional(),
    gpsLongitude: z.number().min(-180).max(180).nullable().optional(),
    gpsCaptureStatus: auditGpsCaptureStatusSchema,
    /** When it was collected on site, not when the server saw it. */
    capturedAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// Audit Execution: findings
// ---------------------------------------------------------------------------
export const findingClassificationSchema = z.enum([
    'conformity',
    'observation',
    'minor_nc',
    'major_nc',
]);
/**
 * Raising a finding.
 *
 * A description is required for every classification, including a conformity: an
 * audit report that says "conformity" with no words has recorded that somebody
 * looked, and nothing about what they saw.
 *
 * `riskLevel` is required for the two non-conformities, because it is half of
 * what spec §11's matrix crosses to reach a priority. Without it the finding has
 * no priority, no SLA and no clock — it would sit in the system looking filed.
 */
export const findingCreateSchema = z
    .object({
    checklistResponseId: z.string().uuid().nullable().optional(),
    classification: findingClassificationSchema,
    clause: z.string().trim().max(50).nullable().optional(),
    description: z.string().trim().min(1, 'Describe what was found.').max(10000),
    evidenceSummary: longText.optional(),
    riskLevel: z.string().trim().max(50).nullable().optional(),
})
    .refine((value) => (value.classification !== 'minor_nc' && value.classification !== 'major_nc') ||
    Boolean(value.riskLevel?.trim()), {
    message: 'A non-conformity needs a risk level — it is what decides the priority and the SLA.',
    path: ['riskLevel'],
});
export const findingUpdateSchema = z.object({
    classification: findingClassificationSchema.optional(),
    clause: z.string().trim().max(50).nullable().optional(),
    description: z.string().trim().min(1).max(10000).optional(),
    evidenceSummary: longText.optional(),
    riskLevel: z.string().trim().max(50).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Post-Audit: corrective action reports
// ---------------------------------------------------------------------------
/**
 * Issuing corrective actions from a finding.
 *
 * A **list** of departments, because ADR-0012 makes that the point: one finding
 * spanning a project team and procurement produces two independently tracked
 * CARs rather than one forced onto a single owner.
 */
export const carIssueSchema = z.object({
    departments: z
        .array(z.object({
        businessUnitId: z.string().uuid(),
        ownerUserId: z.string().uuid().nullable().optional(),
        targetCompletionDate: z.string().date().nullable().optional(),
    }))
        .min(1, 'Name at least one responsible department.')
        .max(20),
});
export const carUpdateSchema = z.object({
    ownerUserId: z.string().uuid().nullable().optional(),
    rootCause: longText.optional(),
    correction: longText.optional(),
    correctiveAction: longText.optional(),
    targetCompletionDate: z.string().date().nullable().optional(),
});
/**
 * Verification.
 *
 * `notes` is required either way. An effective verdict with no words is an
 * auditor's signature on someone else's work with nothing behind it, and an
 * ineffective one with no words tells the department nothing about what to do
 * next.
 */
export const carVerifySchema = z.object({
    effective: z.boolean(),
    notes: z.string().trim().min(1, 'Say what you verified.').max(10000),
    followUpAuditId: z.string().uuid().nullable().optional(),
});
export const carExtensionRequestSchema = z.object({
    until: z.string().date(),
    reason: z.string().trim().min(1, 'Say why the extension is needed.').max(4000),
});
export const auditListQuerySchema = z.object({
    projectId: z.string().uuid().optional(),
    phase: auditPhaseSchema.optional(),
    status: auditStatusSchema.optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    search: z.string().trim().min(1).max(200).optional(),
});
//# sourceMappingURL=audit.js.map